import Storage from './Storage';
import AuthService from './AuthService';
import UserService from './UserService';

// sessionStorage key: the permission list this tab last reloaded for (loop guard, see refresh()).
const RELOADED_FOR_KEY = 'permission-sync-reloaded-for';

const getReloadedFor = () => {
  try {
    return window.sessionStorage.getItem(RELOADED_FOR_KEY);
  } catch (e) {
    return null;
  }
};

const setReloadedFor = (value) => {
  try {
    window.sessionStorage.setItem(RELOADED_FOR_KEY, value);
  } catch (e) {
    // sessionStorage not available: skip the guard
  }
};

/**
 * True when both lists hold the same permissions, whatever their order
 * (the API builds the list with array_agg, which has no fixed order).
 */
const samePermissions = (a, b) => {
  if (!Array.isArray(a) || !Array.isArray(b) || a.length !== b.length) {
    return false;
  }
  const sortedB = [...b].sort();
  return [...a].sort().every((permission, i) => permission === sortedB[i]);
};

export default class PermissionSyncService {
  /**
   * The permission list is saved in localStorage at login, so a role permission
   * changed later (for example from Catalogue Permission) only reached the user
   * after the next login. Call this on page load: it re-reads the permissions
   * from the user-info API, stores them, and reloads the page once if they changed.
   *
   * @param {Array<String>} userTypes user types to refresh, e.g. ['fleet']
   * @return {Promise<Boolean>} true when the permissions changed and the page reloads
   */
  static refresh(userTypes) {
    // The permissions this page was rendered with. Read them now, before any answer arrives:
    // the header's UserBox also calls get-user-info on every page load and saves the new list
    // to storage as soon as it answers, so comparing with storage later would hide the change.
    const rendered = Storage.getUserDetail();
    if (
      !AuthService.isAuthenticated() ||
      !rendered ||
      rendered.temp_fleet ||
      !userTypes.includes(String(rendered.type).toLowerCase())
    ) {
      return Promise.resolve(false);
    }

    return UserService.getUserInfo()
      .then((response) => {
        const fresh = response && response.data && response.data.data;
        const current = Storage.getUserDetail();
        // Ignore the answer if the user logged out, or another user logged in, meanwhile.
        if (
          !fresh ||
          !current ||
          current.uuid !== fresh.uuid ||
          !Array.isArray(fresh.permissions)
        ) {
          return false;
        }
        if (samePermissions(rendered.permissions, fresh.permissions)) {
          return false;
        }
        Storage.setUserDetail({ ...current, permissions: fresh.permissions });

        // Loop guard: reload once per list. If the page still renders with another list after
        // reloading for this one, something else keeps rewriting it, so stop instead of looping.
        const reloadKey = `${fresh.uuid}:${[...fresh.permissions].sort().join(',')}`;
        if (getReloadedFor() === reloadKey) {
          return false;
        }
        setReloadedFor(reloadKey);
        window.location.reload();
        return true;
      })
      .catch((e) => {
        console.error('permission sync failed', e);
        return false;
      });
  }
}
